import React, { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material';
import { Box, Container, Grid, Typography } from '@material-ui/core';
import Navbar from 'components/Navbar';
import Footer from 'components/Footer/Index';
import { IJob } from 'core/interface/job';
import { IMe } from 'core/interface/me';
import NoData from 'components/shared/NoData/Index';
import useLocales from 'hooks/useLocales';
import Pagination from '@material-ui/core/Pagination';
import { JobService } from 'services/jobs/job';
import Image from 'components/image/Image';
import { degreeTypes } from 'core/constants/aboutMePopUpConstants';
import { contractTypes } from 'core/constants/workType';
import PublicJobFilters from './publicJobFilters';
import { getSalaryByValue } from 'constants/constants';
import colors from 'assets/variables/colors';
import HtmlContent from 'components/HtmlContent';
import { getDateFormatter } from 'helpers/dateConverter';
import { getJobLocationDisplayNames } from 'utils/jobIndustriesLocations';
import './index.scss';

const RootStyle = styled('div')(() => ({
  position: 'relative',
  marginTop: '0',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh'
}));

const WrapperContent = styled('div')(() => ({
  position: 'relative',
  flex: 1
}));

const GridStyle = styled(Grid)(() => ({
  '@media (max-width: 959px)': {
    flexWrap: 'wrap',
    '.MuiAutocomplete-fullWidth': {
      width: '100%'
    },
    '.inputs-group': {
      maxWidth: '100%'
    },
    '.MuiAutocomplete-hasPopupIcon': {
      maxWidth: '100%'
    },
    div: {
      maxWidth: '100%'
    }
  }
}));

export const JobCard = styled(Box)(() => ({
  padding: '35px 50px',
  width: '100%',
  background: '#FFFFFF',
  boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.08)',
  borderRadius: '25px',
  marginBottom: '30px',
  cursor: 'pointer'
}));

export const CompetencyTag = styled('div')(
  ({
    tenantColor,
    lighterPurple
  }: {
    tenantColor: string;
    lighterPurple: string;
  }) => ({
    display: 'inline-block',
    padding: '10px 15px',
    margin: '5px',
    backgroundColor: lighterPurple,
    borderRadius: '15px',
    fontSize: '12px',
    fontWeight: 500,
    color: tenantColor
  })
);

export const CompetencyContainer = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  marginTop: '15px',
  marginBottom: '20px'
}));

const JobTitle = styled(Typography)(() => ({
  fontFamily: 'Rubik',
  fontWeight: 600,
  fontSize: '24px',
  color: '#131417',
  marginBottom: '10px',
  textAlign: 'left'
}));

export const JobDetailHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '24px',
  width: '100%',
  marginBottom: '16px'
}));

export const JobDetailHeaderLogo = styled(Box)(() => ({
  flexShrink: 0,
  width: '100px',
  height: '80px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end'
}));

export const JobDetailHeaderContent = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center'
}));

export const JobDetailHeaderLocation = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: '10px'
}));

export const JobDetailHeaderLocationIcon = styled(Box)(() => ({
  display: 'block',
  width: '26px',
  height: '26px',
  borderRadius: '50%',
  position: 'relative',
  backgroundColor: colors.lightPurple,
  flexShrink: 0,
  '& .location': {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)'
  }
}));

export const JobDetailHeaderLocationText = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 400,
  color: '#131417',
  marginLeft: '9px',
  wordBreak: 'break-word'
}));

export const JobPostedDate = styled(Typography)(() => ({
  fontSize: '13px',
  fontWeight: 400,
  color: '#666',
  marginTop: '6px'
}));

export const JobDescription = styled(Typography)(() => ({
  fontSize: '14px',
  color: '#666',
  marginBottom: '15px'
}));

export const JobListDescription = styled(Box)(() => ({
  fontSize: '14px',
  color: '#666',
  marginBottom: '15px',
  width: '100%',
  lineHeight: 1.6,
  wordBreak: 'break-word'
}));

export const JobCardDescription = styled(JobListDescription)(() => ({
  maxHeight: '20em',
  overflowY: 'auto',
  paddingRight: '4px'
}));

export const JobOtherInfo = styled(Box)(() => ({
  width: '100%',
  marginTop: '8px',
  borderTop: '1px solid #eee',
  paddingTop: '20px'
}));

export const JobDetailWrapper = styled(Typography)(() => ({
  fontSize: '14px',
  color: '#333',
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '10px',
  '& svg': {
    marginRight: '8px',
    fontSize: '18px'
  },
  '& strong': {
    marginRight: '5px',
    minWidth: '80px',
    display: 'inline-block'
  }
}));

export const CompetencyTitle = styled(Typography)(() => ({
  fontSize: '16px',
  fontWeight: 600,
  color: '#666',
  marginTop: '20px',
  marginBottom: '20px'
}));

// Add this type for location cache
interface LocationCache {
  [key: string]: string; // locationId -> locationName
}

const PublicJobs: FC = (): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { jobs } = useSelector((state: RootState) => state.jobs);
  const { cities } = useSelector((state: RootState) => state.locations);
  const me = useSelector((state: IMe) => state.user).user;

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [showOnlyMyCompany, setShowOnlyMyCompany] = useState(true);
  const [currentFilters, setCurrentFilters] = useState<any>({});
  // Add state to cache location names
  const [locationCache, setLocationCache] = useState<LocationCache>({});

  const filteredJobs = useMemo(() => {
    if (!jobs?.results) {
      return [];
    }

    if (!showOnlyMyCompany || !me?.company) {
      return jobs.results;
    }

    return jobs.results.filter(
      (job: IJob) => String(job.company?.id) === String(me.company)
    );
  }, [jobs?.results, showOnlyMyCompany, me?.company]);

  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);

  useEffect(() => {
    if (!me) {
      setShowOnlyMyCompany(false);
      return;
    }


    const myCompanyParam = searchParams.get('company');
    const shouldShowMyCompany =
      myCompanyParam === String(me?.company);

    setShowOnlyMyCompany(shouldShowMyCompany);
  }, [me, searchParams]);

  const handleShowOnlyMyCompanyChange = (checked: boolean) => {
    setShowOnlyMyCompany(checked);

    const nextParams = new URLSearchParams(searchParams);
    if (checked) {
      nextParams.set('company', me?.company);
    } else {
      nextParams.delete('company');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const companyParam = searchParams.get('company');

  useEffect(() => {
    getJobs(limit, offset, currentFilters);
    // eslint-disable-next-line
  }, [limit, offset, companyParam]);

  // Fetch location names for job cards when jobs change
  useEffect(() => {
    if (jobs?.results?.length) {
      fetchMissingLocationData(jobs.results);
    }
  }, [jobs?.results]);

  const getJobs = async (
    limit?: number,
    offset?: number,
    filters?: any
  ): Promise<void> => {
    try {
      const params = {
        limit,
        offset,
        public: true,
        ...(companyParam ? { company: Number(companyParam) } : {}),
        ...(filters || {})
      };

      JobService.getJobs(params);
    } catch (error) {
      console.log(error);
    }
  };

  // New function to fetch missing location data
  const fetchMissingLocationData = async (jobsList: IJob[]) => {
    const allLocationIds = new Set<string | number>();
    jobsList.forEach((job) => {
      if (job.locations && job.locations.length) {
        job.locations.forEach((location) => {
          if (
            typeof location === 'object' &&
            location?.display_name
          ) {
            return;
          }
          const locationId =
            typeof location === 'object' ? location.id : location;
          if (locationId && !locationCache[locationId]) {
            allLocationIds.add(locationId);
          }
        });
      }
    });

    // If we have missing locations, fetch them
    if (allLocationIds.size > 0) {
      const locationIdsArray = Array.from(allLocationIds);

      // Fetch each location individually
      const fetchPromises = locationIdsArray.map(async (locationId) => {
        try {
          const locationData = await JobService.getLocationById(locationId);
          if (locationData && locationData.display_name) {
            return { id: locationId, name: locationData.display_name };
          }
        } catch (error) {
          console.error(`Error fetching location ${locationId}:`, error);
        }
        return null;
      });

      // Wait for all fetches to complete
      const results = await Promise.all(fetchPromises);

      // Update cache with results
      const newCache = { ...locationCache };
      results.forEach((result) => {
        if (result) {
          newCache[result.id] = result.name;
        }
      });

      // Update cache
      setLocationCache(newCache);
    }
  };

  const getLocationNames = (locations: IJob['locations']) => {
    const names = getJobLocationDisplayNames(locations, []);
    if (names.length) {
      return names;
    }

    return (locations || [])
      .map((location) => {
        const id = typeof location === 'object' ? location.id : location;
        return id ? locationCache[id] || '' : '';
      })
      .filter(Boolean);
  };

  const renderJobCard = (job: IJob) => {
    const locationNames = getLocationNames(job.locations);

    return (
      <JobCard
        key={job.id}
        onClick={() => navigate(`/jobs/${job.slug || job.id}`)}
      >
        <JobDetailHeader>
          <JobDetailHeaderContent>
            <JobTitle variant="h5" style={{ marginBottom: 0 }}>
              {job.title}
            </JobTitle>
            {job.created_at && (
              <JobPostedDate variant="body2" style={{ marginBottom: '8px' }}>
                {getDateFormatter(job.created_at, 'MMM DD YYYY')}
              </JobPostedDate>
            )}
            {job.company?.name && (
              <JobDetailHeaderLocation>
                <JobDetailHeaderLocationIcon>
                  <Image
                    className="location"
                    name="portfolio.svg"
                    width={14}
                    height={14}
                  />
                </JobDetailHeaderLocationIcon>
                <JobDetailHeaderLocationText variant="body2">
                  {job.company.name}
                </JobDetailHeaderLocationText>
              </JobDetailHeaderLocation>
            )}
            {locationNames.length > 0 && (
              <JobDetailHeaderLocation>
                <JobDetailHeaderLocationIcon>
                  <Image
                    className="location"
                    name="location.svg"
                    width={12}
                    height={12}
                  />
                </JobDetailHeaderLocationIcon>
                <JobDetailHeaderLocationText variant="body2">
                  {locationNames.join(', ')}
                </JobDetailHeaderLocationText>
              </JobDetailHeaderLocation>
            )}
          </JobDetailHeaderContent>
          <JobDetailHeaderLogo>
            <Image
              name={
                job.company.logo || '/static/images/recrout-logo-letter.jpg'
              }
              url={
                job.company.logo || '/static/images/recrout-logo-letter.jpg'
              }
              style={{
                width: 'auto',
                maxWidth: '100px',
                height: 'auto',
                maxHeight: '80px',
                objectFit: 'contain'
              }}
            />
          </JobDetailHeaderLogo>
        </JobDetailHeader>

        <JobCardDescription>
          <HtmlContent
            content={job.description || ''}
            className="jobdescription__publicjobs"
          />
        </JobCardDescription>

        <JobOtherInfo>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {job.contract && (
                <JobDetailWrapper>
                  <strong>{t('Contract')}: </strong>
                  {Array.isArray(job.contract)
                    ? job.contract
                        .map(
                          (c) =>
                            contractTypes.find((type) => type.value === c)
                              ?.to_show || ''
                        )
                        .filter(Boolean)
                        .join(', ')
                    : contractTypes.find((c) => c.value === job.contract)
                        ?.to_show || ''}
                </JobDetailWrapper>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {job.compensation && (
                <JobDetailWrapper>
                  <strong>{t('Compensations (EUR)')}: </strong>{' '}
                  {Array.isArray(job.compensation)
                    ? job.compensation
                        .map((comp) => getSalaryByValue(comp))
                        .filter(Boolean)
                        .join(', ')
                    : getSalaryByValue(job.compensation)}
                </JobDetailWrapper>
              )}
              {job.degree && (
                <JobDetailWrapper>
                  <strong>{t('Education')}: </strong>
                  {degreeTypes.find((d) => d.value === job.degree)?.to_show ||
                    ''}
                </JobDetailWrapper>
              )}
              {job.work_exp && (
                <JobDetailWrapper>
                  <strong>{t('Experience')}: </strong> {job.work_exp}{' '}
                  {t('years')}
                </JobDetailWrapper>
              )}
            </Grid>
          </Grid>
          <CompetencyTitle>
            {t('We are looking for these competencies')}:
          </CompetencyTitle>
          {job.competencies && job.competencies.length > 0 && (
            <CompetencyContainer>
              {job.competencies.map((comp, index) => (
                <CompetencyTag
                  key={index}
                  tenantColor={colors.tenantColor}
                  lighterPurple={colors.lightPurple}
                >
                  {comp.competency.name}
                </CompetencyTag>
              ))}
            </CompetencyContainer>
          )}
        </JobOtherInfo>
      </JobCard>
    );
  };

  return (
    <RootStyle>
      <Navbar />
      <div
        className="public-companies-banner"
        style={{ backgroundColor: colors.tenantColor }}
      >
        <div className="banner-content">
          <h1>{t('Discover Great career opportunities')}</h1>
          <p>
            {t('Browse through our latest job listings and find your next career opportunity')}
          </p>
        </div>
      </div>
      <WrapperContent>
        <Container
          maxWidth="lg"
          style={{ paddingTop: '50px', paddingBottom: '50px' }}
        >
          <div
            className="back_buttons skillio-back-button"
            onClick={() => navigate(-1)}
          >
            <Image name="arrow-left.svg" />
            {t('Back')}
          </div>
          <GridStyle container spacing={3}>
            <Grid item xs={12} lg={3} className="inputs-group">
              <PublicJobFilters
                applyFilter={(filters) => {
                  setCurrentFilters(filters || {});
                  setPage(1);
                  getJobs(limit, 0, filters);
                }}
                showMyCompanyFilter={!!me?.company}
                showOnlyMyCompany={showOnlyMyCompany}
                onShowOnlyMyCompanyChange={handleShowOnlyMyCompanyChange}
              />
            </Grid>
            <Grid item xs={12} lg={9}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                {!filteredJobs.length ? (
                  <NoData />
                ) : (
                  <>
                    {filteredJobs.map((job: IJob) => renderJobCard(job))}
                    <Box display="flex" justifyContent="center" mt={4}>
                      <Pagination
                        className="pagination"
                        count={Math.ceil((jobs?.count || 0) / limit)}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        variant="outlined"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </GridStyle>
        </Container>
      </WrapperContent>
      <Footer />
    </RootStyle>
  );
};

export default PublicJobs;
